<template>
  <div class="admin-page">
    <div class="admin-toolbar">
      <div class="admin-toolbar__actions admin-toolbar__actions--wrap">
        <el-button v-if="isCreatorWorkspace" @click="goBackCreator">返回我的创作</el-button>
        <el-radio-group v-model="filters.period" @change="onPeriodChange">
          <el-radio-button label="all">全部</el-radio-button>
          <el-radio-button label="today">当天</el-radio-button>
          <el-radio-button label="week">当周</el-radio-button>
          <el-radio-button label="month">当月</el-radio-button>
          <el-radio-button label="range">时间段</el-radio-button>
        </el-radio-group>
        <el-date-picker
          v-if="filters.period === 'range'"
          v-model="filters.dateRange"
          type="datetimerange"
          range-separator="至"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          value-format="x"
          class="filter-date-range"
          @change="fetchList"
        />
        <el-input
          v-if="!lockedExamId"
          v-model="filters.examName"
          placeholder="测评名称"
          clearable
          class="filter-input"
          @keyup.enter="fetchList"
        />
        <el-tag v-else type="info" effect="plain">当前测评 ID {{ lockedExamId }}</el-tag>
        <el-input
          v-model="filters.username"
          placeholder="用户名 / 昵称"
          clearable
          class="filter-input"
          @keyup.enter="fetchList"
        />
        <el-input
          v-model="filters.userUuid"
          placeholder="用户 UUID"
          clearable
          class="filter-input-wide"
          @keyup.enter="fetchList"
        />
        <el-button type="primary" @click="fetchList">查询</el-button>
      </div>
    </div>

    <el-table :data="results" v-loading="loading" style="width: 100%">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column label="用户" min-width="150">
        <template #default="scope">
          <div>{{ scope.row.nickname || scope.row.username || '—' }}</div>
          <div class="muted uuid-line">{{ scope.row.userUuid || '—' }}</div>
        </template>
      </el-table-column>
      <el-table-column prop="examName" label="测评" min-width="160" show-overflow-tooltip />
      <el-table-column label="用时" width="100">
        <template #default="scope">{{ formatDuration(scope.row.totalTime) }}</template>
      </el-table-column>
      <el-table-column label="完成时间" width="170">
        <template #default="scope">{{ formatTime(scope.row.createdAt) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="100" fixed="right">
        <template #default="scope">
          <el-button type="primary" link @click="viewDetail(scope.row)">查看结果</el-button>
        </template>
      </el-table-column>
    </el-table>

    <AdminPagination
      v-model:current-page="page"
      v-model:page-size="pageSize"
      :total="total"
      @size-change="fetchList"
      @current-change="fetchList"
    />
  </div>
</template>

<script>
import axios from 'axios'
import { useRouter } from 'vue-router'
import AdminPagination from '@/components/AdminPagination.vue'
import { authHeaders } from '@/utils/http'
import { formatDuration } from '@/utils/formatDuration'

export default {
  name: 'AdminExamResultManagement',
  components: { AdminPagination },
  setup() {
    const router = useRouter()
    return { router }
  },
  data() {
    return {
      loading: false,
      results: [],
      total: 0,
      page: 1,
      pageSize: 20,
      filters: {
        period: 'today',
        dateRange: null,
        examName: '',
        username: '',
        userUuid: ''
      }
    }
  },
  computed: {
    isCreatorWorkspace() {
      return this.$route.meta.creatorExamResults === true
    },
    lockedExamId() {
      if (this.isCreatorWorkspace) {
        return Number(this.$route.params.id) || 0
      }
      const q = this.$route.query.examId
      return q ? Number(q) || 0 : 0
    }
  },
  watch: {
    '$route.query.examId'() {
      this.page = 1
      this.fetchList()
    }
  },
  created() {
    if (this.lockedExamId) {
      this.filters.period = 'all'
    }
    this.fetchList()
  },
  methods: {
    formatDuration,
    goBackCreator() {
      this.$router.push({ path: '/creator/works', query: { tab: 'exam' } })
    },
    formatTime(ts) {
      if (!ts) return '—'
      return new Date(ts * 1000).toLocaleString()
    },
    onPeriodChange() {
      if (this.filters.period !== 'range') {
        this.filters.dateRange = null
      }
      this.page = 1
      this.fetchList()
    },
    buildPayload() {
      const payload = {
        page: this.page,
        pageSize: this.pageSize
      }
      if (this.filters.period && this.filters.period !== 'all') {
        payload.period = this.filters.period
      }
      if (this.lockedExamId) payload.examId = this.lockedExamId
      if (!this.lockedExamId && this.filters.examName) payload.examName = this.filters.examName
      if (this.filters.username) payload.username = this.filters.username
      if (this.filters.userUuid) payload.userUuid = this.filters.userUuid
      if (this.filters.period === 'range' && this.filters.dateRange?.length === 2) {
        payload.startAt = Math.floor(Number(this.filters.dateRange[0]) / 1000)
        payload.endAt = Math.floor(Number(this.filters.dateRange[1]) / 1000)
      }
      return payload
    },
    async fetchList() {
      if (this.filters.period === 'range') {
        if (!this.filters.dateRange || this.filters.dateRange.length !== 2) {
          this.$message.warning('请选择时间段')
          return
        }
      }
      this.loading = true
      try {
        const res = await axios.post(
          '/api/v1/admin/exam/result/list',
          this.buildPayload(),
          { headers: authHeaders() }
        )
        if (res.data.code !== 200) {
          this.$message.error(res.data.message || '加载失败')
          return
        }
        this.results = res.data.data?.results || []
        this.total = res.data.data?.total || 0
      } catch (e) {
        this.$message.error(e.response?.data?.message || '加载失败')
      } finally {
        this.loading = false
      }
    },
    viewDetail(row) {
      if (this.isCreatorWorkspace) {
        this.router.push(`/creator/exam-results/${row.id}`)
      } else {
        const query = this.lockedExamId ? { examId: String(this.lockedExamId) } : undefined
        this.router.push({ path: `/admin/exam-results/${row.id}`, query })
      }
    }
  }
}
</script>

<style scoped>
.muted {
  color: #909399;
  font-size: 12px;
}
.uuid-line {
  word-break: break-all;
}
.filter-date-range {
  max-width: 360px;
}
</style>
